import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLoginModal } from '../../context/LoginModalContext';
import { useSettings } from '../../context/SettingsContext';
import hiveService from '../../services/hive';
import { FEED_AUTHORS, FEED_DISPLAY_CONFIG } from '../../config/feed-details';
import VoteSlider from '../VoteSlider/VoteSlider';
import './FeedContainerVoteNotice.css';
import usePostBlockchainData from '../../services/usePostBlockchainData';

const DISMISS_KEY_PREFIX = 'app-feed-container-vote-dismissed-';

const getContainerIdentity = (container) => {
  if (!container?.author || !container?.permlink) return '';
  return `${container.author}/${container.permlink}`;
};

const getDismissStorageKey = (username, feedType) => `${DISMISS_KEY_PREFIX}${username || 'guest'}-${feedType}`;

const isSupportedFeedType = (feedType) => {
  if (!feedType || feedType === 'global' || feedType === 'trending') return false;
  return true;
};

const FeedContainerVoteNotice = ({ feedType, onVoted, isFeedReady = true }) => {
  const { user, isAuthenticated } = useAuth();
  const { openLoginModal } = useLoginModal();
  const { voteDefaultWeight } = useSettings();
  const [containerPost, setContainerPost] = useState(null);
  const [voteWeight, setVoteWeight] = useState(voteDefaultWeight || 100);
  const [loading, setLoading] = useState(true);
  const [isVoting, setIsVoting] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isSliderExpanded, setIsSliderExpanded] = useState(false);
  const [error, setError] = useState('');

  const {
    hasVoted,
    cashoutTime,
    castVote
  } = usePostBlockchainData({
    post: containerPost,
    user,
    isAuthenticated
  });

  useEffect(() => {
    setVoteWeight(voteDefaultWeight || 100);
  }, [voteDefaultWeight]);

  const feedLabel = FEED_DISPLAY_CONFIG[feedType]?.label || 'feed';
  const containerIdentity = useMemo(() => getContainerIdentity(containerPost), [containerPost]);

  useEffect(() => {
    let cancelled = false;

    const loadContainer = async () => {
      if (!isSupportedFeedType(feedType)) {
        setContainerPost(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      setDismissed(false);

      try {
        const container = await hiveService.getLatestContainer(feedType);
        if (cancelled) return;

        if (!container?.author || !container?.permlink || !FEED_AUTHORS.includes(container.author)) {
          setContainerPost(null);
          return;
        }

        setContainerPost(container);
      } catch (loadError) {
        if (!cancelled) {
          setContainerPost(null);
          setError(loadError.message || 'Failed to load container post');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadContainer();

    return () => {
      cancelled = true;
    };
  }, [feedType]);

  useEffect(() => {
    if (!containerIdentity) return;
    const storageKey = getDismissStorageKey(user?.username, feedType);
    const dismissedIdentity = localStorage.getItem(storageKey);
    setDismissed(dismissedIdentity === containerIdentity);
  }, [containerIdentity, feedType, user?.username]);

  const handleDismiss = useCallback(() => {
    if (!containerIdentity) return;
    const storageKey = getDismissStorageKey(user?.username, feedType);
    localStorage.setItem(storageKey, containerIdentity);
    setDismissed(true);
  }, [containerIdentity, feedType, user?.username]);

  const handleVote = useCallback(async (_isUpvote = true, explicitWeight = null) => {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }

    if (!user?.username || !containerPost?.author || !containerPost?.permlink) {
      return;
    }

    const percent = explicitWeight ?? voteWeight;

    setIsVoting(true);
    try {
      await castVote(percent, true);
      setDismissed(true);
      const storageKey = getDismissStorageKey(user.username, feedType);
      localStorage.removeItem(storageKey);
      if (typeof onVoted === 'function') {
        onVoted({ author: containerPost.author, permlink: containerPost.permlink, weight: percent });
      }
    } catch (voteError) {
      setError(voteError.message || 'Failed to submit vote');
    } finally {
      setIsVoting(false);
    }
  }, [containerPost, feedType, isAuthenticated, onVoted, openLoginModal, user, voteWeight, castVote]);

  const containerIsOpenForPayout = cashoutTime ? cashoutTime.getTime() > Date.now() : false;

  if (!isFeedReady || !isAuthenticated || !user?.username || !isSupportedFeedType(feedType) || loading || !containerPost || !cashoutTime || !containerIsOpenForPayout || dismissed || hasVoted) {
    return null;
  }

  return (
    <section className="feed-container-vote-notice" aria-label="Container vote notice">
      <div className="feed-container-vote-notice__header">
        <div>
          <p className="feed-container-vote-notice__eyebrow">Support the current {feedLabel} container</p>
          <h3 className="feed-container-vote-notice__title">
            Vote on <Link to={`/@${containerPost.author}/${containerPost.permlink}`}>@{containerPost.author}/{containerPost.permlink}</Link>
          </h3>
        </div>
        <button
          type="button"
          className="feed-container-vote-notice__dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss vote notice"
        >
          ×
        </button>
      </div>

      {/*<p className="feed-container-vote-notice__body">
        This feed is anchored to the latest container post. If you have not voted on it yet, you can set your weight here without opening the detail page.
      </p>*/}

      {error && <div className="feed-container-vote-notice__error">{error}</div>}

      <div className="feed-container-vote-notice__quick-vote">
          <button
            className="confirm-btn"
            onClick={() => handleVote(true, voteWeight)}
            disabled={isVoting}
          >
            {isVoting ? 'Voting...' : `Vote (${voteWeight}%)`}
          </button>
          <button
            className={`secondary-btn feed-container-vote-notice__toggle-btn ${isSliderExpanded ? 'expanded' : ''}`}
            onClick={() => setIsSliderExpanded(!isSliderExpanded)}
            title="Adjust Weight"
          >
            Adjust Weight
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feed-container-vote-notice__toggle-icon">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          <button
            className="secondary-btn vote-dismiss-btn"
            onClick={handleDismiss}
          >
            Not now
          </button>
      </div>

      {isSliderExpanded && (
        <div className="feed-container-vote-notice__slider">
          <VoteSlider
            inline={true}
            voteWeight={voteWeight}
            setVoteWeight={setVoteWeight}
            onCancel={() => setIsSliderExpanded(false)}
            onVote={handleVote}
            isVoting={isVoting}
            userHasVoted={false}
            userVoteWeight={null}
          />
        </div>
      )}
    </section>
  );
};

export default FeedContainerVoteNotice;
